import { clstx } from '@payaca/utilities/cvaMerge';
import { Content, Extensions, useEditor } from '@tiptap/react';
import { useEffect, useMemo, useRef } from 'react';

import { RichTextEditorVariables } from '@payaca/types/richTextEditorVariables';

import {
  DEFAULT_INLINE_TOOLBAR,
  DEFAULT_TOOLBAR,
  type EditorToolbarItem,
} from './editorActions';
import { baseEditorExtensions } from './editorExtensions';
import EditorShell from './EditorShell';
import { VariableNode } from './nodes/variable';
import VariableList from './VariableList';

import Placeholder from '@tiptap/extension-placeholder';
import type { JSX } from 'react';

export type RichTextareaToolbarItem = EditorToolbarItem;

type OnChangeType<IsHtmlOutput extends boolean> = IsHtmlOutput extends true
  ? string
  : object | object[] | null;

export type RichTextContent = Content;

export interface RichTextareaProps<
  T extends string,
  IsHtmlOutput extends boolean,
> {
  value: RichTextContent;
  onChange?: (value: OnChangeType<IsHtmlOutput>) => void;
  variables?: RichTextEditorVariables<T>;
  readOnly?: boolean;
  isHtmlOutput?: IsHtmlOutput;
  className?: string;
  /**
   * Render the editor inline with no surrounding frame (no border/box) and no
   * padding, so it sits directly on the page. Formatting is surfaced via a
   * bubble menu on text selection rather than a static toolbar. Has no effect
   * when `readOnly`.
   */
  borderless?: boolean;
  /**
   * Pass `false` to hide the formatting controls entirely. Pass an array to
   * customise which actions are offered — in the static toolbar by default, or
   * in the bubble menu when `borderless`. Defaults to `DEFAULT_TOOLBAR`
   * (`DEFAULT_INLINE_TOOLBAR` when `borderless`). An explicit array opts out of
   * that curation, so in `borderless` mode it may include document-level items
   * (e.g. `undo`/`redo`) that the inline default deliberately omits.
   */
  toolbar?: RichTextareaToolbarItem[] | false;
  placeholder?: string;
  /**
   * Accessible name for the editor. TipTap's contenteditable can't be
   * associated with a `<label>` element, so pass the field's label text here.
   */
  'aria-label'?: string;
}

/**
 * A rich text editor backed by TipTap, with optional variable templating.
 *
 * ## When to use
 *
 * - Formatted content that needs `{{customer.name}}`-style variable
 *   interpolation (e.g. email/document templates)
 * - `value` is `Content` (TipTap JSON or HTML). Store as TipTap JSON in the
 *   database.
 *
 * ## When to choose another component
 *
 * - **Persisting markdown in the database** — use `MarkdownTextarea`. Its
 *   `value` is a markdown `string` and it round-trips byte-stable. `underline`
 *   is not supported (markdown can't represent it).
 * - **Plain multi-line text with no formatting** — use `Textarea`.
 *
 * ## Composition rules
 *
 * Both `RichTextarea` and `MarkdownTextarea` accept:
 *
 * - `borderless` — render inline with no frame or padding (it sits directly on
 *   the page); formatting is then surfaced via a bubble menu on text selection
 *   rather than a static toolbar.
 * - `placeholder` — text shown while the editor is empty.
 */
const RichTextarea: {
  (props: RichTextareaProps<string, true>): JSX.Element;
  (props: RichTextareaProps<string, false>): JSX.Element;
} = <T extends string, IsHtmlOutput extends boolean>(
  props: RichTextareaProps<T, IsHtmlOutput>
) => {
  const {
    variables,
    value,
    onChange,
    readOnly = false,
    isHtmlOutput = false,
    className,
    borderless = false,
    toolbar,
    placeholder,
    'aria-label': ariaLabel,
  } = props;

  const toolbarItems =
    toolbar ?? (borderless ? DEFAULT_INLINE_TOOLBAR : DEFAULT_TOOLBAR);

  // Track the last value we emitted via onChange so the value-sync effect
  // can skip the controlled-component echo path. Compared by JSON to handle
  // both string (HTML) and object (TipTap JSON) outputs uniformly.
  const lastEmittedJsonRef = useRef<string>(JSON.stringify(value));

  const extensions = useMemo(() => {
    const root: Extensions = [...baseEditorExtensions, VariableNode];

    if (placeholder) {
      root.push(Placeholder.configure({ placeholder: placeholder }));
    }

    return root;
  }, [placeholder]);

  const editor = useEditor({
    onUpdate: ({ editor }) => {
      const v = isHtmlOutput ? editor.getHTML() : editor.getJSON();
      lastEmittedJsonRef.current = JSON.stringify(v);
      onChange?.(v as OnChangeType<IsHtmlOutput>);
    },
    extensions,
    content: value,
    editorProps: {
      attributes: {
        // ProseMirror's editable is a `contenteditable` div; expose it to
        // assistive tech (and role/label-based test locators) as a multiline
        // textbox per WAI-ARIA.
        role: 'textbox',
        'aria-multiline': 'true',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        class: clstx(
          'tiptap-editor',
          'typography:prose focus:outline-none',
          !readOnly && !borderless ? 'p-3' : '',
          className
        ),
      },
    },
    editable: !readOnly,
  });

  // Toggle editability without rebuilding the editor — preserves undo
  // history, focus, and scroll position when readOnly flips at runtime.
  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== !readOnly) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  // Sync external `value` changes (e.g. form reset, switching the
  // displayed entity) into the editor. Skip the controlled-component echo
  // path and skip when the editor already matches.
  useEffect(() => {
    if (!editor) return;
    const incoming = JSON.stringify(value);
    if (incoming === lastEmittedJsonRef.current) return;
    const current = isHtmlOutput ? editor.getHTML() : editor.getJSON();
    if (JSON.stringify(current) === incoming) return;
    editor.commands.setContent(value, { emitUpdate: false });
    lastEmittedJsonRef.current = incoming;
  }, [editor, value, isHtmlOutput]);

  return (
    <EditorShell
      editor={editor}
      readOnly={readOnly}
      toolbar={toolbarItems}
      borderless={borderless}
      sidebar={
        variables?.length ? (
          <VariableList<T>
            editor={editor}
            variables={variables}
            className="md:w-1/4"
          />
        ) : undefined
      }
    />
  );
};

export default RichTextarea;
